import { type FormEvent, useEffect, useId, useRef, useState } from 'react';

import {
  type Campaign,
  type CampaignColor,
  type CampaignInput,
  type CampaignPlate,
  createCampaign,
  deleteCampaign,
  setCampaignArchived,
  updateCampaign,
} from '../api.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { describeError } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { RowList } from '../ProductProfile.tsx';
import { useToast } from '../toast.tsx';
import { Badge } from '../ui.tsx';
import * as wording from '../wording.ts';
import { CAMPAIGN_COLOR_VAR, shortDay } from './lines.ts';

/** Design reference § Data model and § Validation: the server's ceilings. */
const MAX_NAME_LENGTH = 60;
const MAX_GOAL_LENGTH = 500;
const MAX_NOTES_LENGTH = 2000;
export const MAX_TEMPLATE_ITEMS = 20;
const MAX_TEMPLATE_ITEM_LENGTH = 120;

/** The seven swatches, in the order of § Campaign colours. */
const COLORS = Object.keys(CAMPAIGN_COLOR_VAR) as CampaignColor[];

/** The form as typed: the checklist template is one item per line. */
interface Draft {
  name: string;
  color: CampaignColor;
  brandBookId: string;
  startsOn: string;
  endsOn: string;
  goal: string;
  notes: string;
  checklist: string;
  plates: CampaignPlate[];
}

const NEW_DRAFT: Draft = {
  name: '',
  color: 'blue',
  brandBookId: '',
  startsOn: '',
  endsOn: '',
  goal: '',
  notes: '',
  checklist: '',
  plates: [],
};

function draftOf(campaign: Campaign): Draft {
  return {
    name: campaign.name,
    color: campaign.color,
    brandBookId: campaign.brandBookId ?? '',
    startsOn: campaign.startsOn ?? '',
    endsOn: campaign.endsOn ?? '',
    goal: campaign.goal ?? '',
    notes: campaign.notes ?? '',
    checklist: campaign.checklist.join('\n'),
    plates: campaign.plates,
  };
}

/** The template's items: one per line, trimmed, blank lines dropped. */
export function templateItems(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

function blankToNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

/** The plates as typed, trimmed, rows left wholly blank dropped. */
export function plateRows(plates: readonly CampaignPlate[]): CampaignPlate[] {
  return plates
    .map((plate) => ({ ...plate, name: plate.name.trim(), libraryItemId: plate.libraryItemId.trim() }))
    .filter((plate) => plate.name !== '' || plate.libraryItemId !== '');
}

/** What is wrong with a draft before it is sent, or null. */
function draftProblem(draft: Draft): string | null {
  if (plateRows(draft.plates).some((plate) => plate.name === '' || plate.libraryItemId === '')) {
    return wording.CAMPAIGN_PLATE_INCOMPLETE;
  }
  const items = templateItems(draft.checklist);
  if (items.length > MAX_TEMPLATE_ITEMS) return wording.campaignChecklistTooLong(items.length, MAX_TEMPLATE_ITEMS);
  if (items.some((item) => item.length > MAX_TEMPLATE_ITEM_LENGTH)) {
    return wording.campaignChecklistItemTooLong(MAX_TEMPLATE_ITEM_LENGTH);
  }
  if (draft.startsOn !== '' && draft.endsOn !== '' && draft.endsOn < draft.startsOn) {
    return wording.CAMPAIGN_ENDS_BEFORE_START;
  }
  return null;
}

function inputOf(draft: Draft): CampaignInput {
  return {
    name: draft.name.trim(),
    color: draft.color,
    brandBookId: draft.brandBookId === '' ? null : draft.brandBookId,
    startsOn: draft.startsOn === '' ? null : draft.startsOn,
    endsOn: draft.endsOn === '' ? null : draft.endsOn,
    goal: blankToNull(draft.goal),
    notes: blankToNull(draft.notes),
    checklist: templateItems(draft.checklist),
    plates: plateRows(draft.plates),
  };
}

/** The cards a campaign holds on the board: its open ideas and its contents. */
function cardCount(campaign: Campaign): number {
  return campaign.counts.ideas + campaign.counts.contents;
}

/**
 * Managing campaigns from the board (US-030), on the native `<dialog>` like
 * `ShortcutsDialog`: a list of every campaign, and a form for a new one or the
 * one clicked. The campaigns come from the board's own read, so `onChanged`
 * (the board's refresh) is how a save shows up here and in the lanes at once.
 */
export function CampaignsDialog({
  open,
  campaigns,
  brandBooks,
  onClose,
  onChanged,
}: {
  readonly open: boolean;
  /** Every campaign, archived ones included. */
  readonly campaigns: readonly Campaign[];
  readonly brandBooks: readonly { id: string; name: string }[];
  readonly onClose: () => void;
  readonly onChanged: () => Promise<void> | void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const toast = useToast();
  const id = useId();
  /** `null` is the list, `new` the empty form, else the id of the campaign being edited. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(NEW_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Each opening starts on the list.
  useEffect(() => {
    if (!open) return;
    setEditing(null);
    setError(null);
    setDeleting(false);
  }, [open]);

  /** The campaign being edited as the board last read it; null for a new one. */
  const current = editing === null || editing === 'new' ? null : (campaigns.find((one) => one.id === editing) ?? null);

  const edit = (campaign: Campaign | null): void => {
    setEditing(campaign === null ? 'new' : campaign.id);
    setDraft(campaign === null ? NEW_DRAFT : draftOf(campaign));
    setError(null);
  };

  const backToList = (): void => {
    setEditing(null);
    setError(null);
  };

  const change = <K extends keyof Draft>(field: K, value: Draft[K]): void => {
    setDraft((previous) => ({ ...previous, [field]: value }));
  };

  const problem = draftProblem(draft);

  const save = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy || draft.name.trim() === '' || problem !== null) return;
    setBusy(true);
    setError(null);
    try {
      const input = inputOf(draft);
      const saved = current === null ? await createCampaign(input) : await updateCampaign(current.id, input);
      toast.ok(wording.campaignSaved(saved.name));
      await onChanged();
      backToList();
    } catch (cause: unknown) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const toggleArchived = async (campaign: Campaign): Promise<void> => {
    const archive = campaign.archivedAt === null;
    setBusy(true);
    setError(null);
    try {
      const saved = await setCampaignArchived(campaign.id, archive);
      toast.ok(wording.campaignArchived(saved.name, archive));
      await onChanged();
    } catch (cause: unknown) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (campaign: Campaign): Promise<void> => {
    setBusy(true);
    try {
      await deleteCampaign(campaign.id);
      toast.ok(wording.campaignDeleted(campaign.name));
      setDeleting(false);
      await onChanged();
      backToList();
    } catch (cause: unknown) {
      setDeleting(false);
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const titleId = `${id}-title`;
  // A campaign deleted elsewhere while its form was open: back to the list, never a silent create.
  const onList = editing === null || (editing !== 'new' && current === null);

  return (
    <dialog
      className="dialog campaigns-dialog"
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(event) => {
        // Esc on the form goes back to the list; on the list it closes.
        event.preventDefault();
        if (busy) return;
        if (!onList) backToList();
        else onClose();
      }}
    >
      {onList ? (
        <>
          <h2 className="dialog__title" id={titleId}>
            {wording.CAMPAIGNS}
          </h2>
          <div className="dialog__body">
            {campaigns.length === 0 ? (
              <p className="campaigns-dialog__empty">{wording.CAMPAIGNS_EMPTY}</p>
            ) : (
              <ul className="campaigns-dialog__list">
                {campaigns.map((campaign) => {
                  const range = wording.campaignRange(
                    campaign.startsOn === null ? null : shortDay(campaign.startsOn),
                    campaign.endsOn === null ? null : shortDay(campaign.endsOn),
                  );
                  return (
                    <li key={campaign.id}>
                      <button
                        type="button"
                        className="campaigns-dialog__row"
                        onClick={() => {
                          edit(campaign);
                        }}
                      >
                        <span
                          className="board__lane-dot"
                          style={{ backgroundColor: CAMPAIGN_COLOR_VAR[campaign.color] }}
                          aria-hidden="true"
                        />
                        <span className="campaigns-dialog__name">{campaign.name}</span>
                        {campaign.archivedAt !== null && <Badge>{wording.CAMPAIGN_ARCHIVED}</Badge>}
                        <span className="campaigns-dialog__meta">
                          {range !== null && <span>{range}</span>}
                          <span>{wording.campaignCounts(campaign.counts)}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="dialog__actions">
            <button type="button" className="button" onClick={onClose}>
              {wording.CAMPAIGN_CLOSE}
            </button>
            <button
              type="button"
              className="button button--primary"
              autoFocus
              onClick={() => {
                edit(null);
              }}
            >
              <Icon name="plus" />
              {wording.CAMPAIGN_NEW}
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={(event) => void save(event)}>
          <h2 className="dialog__title" id={titleId}>
            {current === null ? wording.CAMPAIGN_NEW : wording.CAMPAIGN_EDIT}
            {current !== null && current.archivedAt !== null && (
              <>
                {' '}
                <Badge>{wording.CAMPAIGN_ARCHIVED}</Badge>
              </>
            )}
          </h2>
          <div className="dialog__body campaigns-dialog__form">
            <button type="button" className="button button--quiet campaigns-dialog__back" onClick={backToList}>
              <Icon name="arrow-left" />
              {wording.CAMPAIGN_BACK}
            </button>
            <div className="field">
              <label className="field__label" htmlFor={`${id}-name`}>
                {wording.CAMPAIGN_NAME}
              </label>
              <input
                id={`${id}-name`}
                className="field__input"
                type="text"
                required
                autoFocus
                autoComplete="off"
                maxLength={MAX_NAME_LENGTH}
                value={draft.name}
                onChange={(event) => {
                  change('name', event.target.value);
                }}
              />
            </div>
            <fieldset className="field campaigns-dialog__colors">
              <legend className="field__label">{wording.CAMPAIGN_COLOR}</legend>
              <div className="campaigns-dialog__swatches">
                {COLORS.map((color) => (
                  <label key={color} className="campaign-swatch" title={wording.CAMPAIGN_COLOR_NAME[color]}>
                    <input
                      type="radio"
                      name={`${id}-color`}
                      value={color}
                      checked={draft.color === color}
                      aria-label={wording.CAMPAIGN_COLOR_NAME[color]}
                      onChange={() => {
                        change('color', color);
                      }}
                    />
                    <span
                      className="campaign-swatch__chip"
                      style={{ backgroundColor: CAMPAIGN_COLOR_VAR[color] }}
                      aria-hidden="true"
                    />
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="field">
              <label className="field__label" htmlFor={`${id}-book`}>
                {wording.CAMPAIGN_BRAND_BOOK}
              </label>
              <select
                id={`${id}-book`}
                className="field__input"
                value={draft.brandBookId}
                onChange={(event) => {
                  change('brandBookId', event.target.value);
                }}
              >
                <option value="">{wording.CAMPAIGN_NO_BRAND_BOOK}</option>
                {brandBooks.map((book) => (
                  <option key={book.id} value={book.id}>
                    {book.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="campaigns-dialog__dates">
              <div className="field">
                <label className="field__label" htmlFor={`${id}-starts`}>
                  {wording.CAMPAIGN_STARTS_ON}
                </label>
                <input
                  id={`${id}-starts`}
                  className="field__input"
                  type="date"
                  value={draft.startsOn}
                  onChange={(event) => {
                    change('startsOn', event.target.value);
                  }}
                />
              </div>
              <div className="field">
                <label className="field__label" htmlFor={`${id}-ends`}>
                  {wording.CAMPAIGN_ENDS_ON}
                </label>
                <input
                  id={`${id}-ends`}
                  className="field__input"
                  type="date"
                  value={draft.endsOn}
                  onChange={(event) => {
                    change('endsOn', event.target.value);
                  }}
                />
              </div>
            </div>
            <div className="field">
              <label className="field__label" htmlFor={`${id}-goal`}>
                {wording.CAMPAIGN_GOAL}
              </label>
              <textarea
                id={`${id}-goal`}
                className="field__input"
                rows={2}
                maxLength={MAX_GOAL_LENGTH}
                value={draft.goal}
                onChange={(event) => {
                  change('goal', event.target.value);
                }}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor={`${id}-notes`}>
                {wording.CAMPAIGN_NOTES}
              </label>
              <textarea
                id={`${id}-notes`}
                className="field__input"
                rows={3}
                maxLength={MAX_NOTES_LENGTH}
                value={draft.notes}
                onChange={(event) => {
                  change('notes', event.target.value);
                }}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor={`${id}-checklist`}>
                {wording.CAMPAIGN_CHECKLIST}
              </label>
              <textarea
                id={`${id}-checklist`}
                className="field__input"
                rows={4}
                value={draft.checklist}
                aria-describedby={`${id}-checklist-hint`}
                onChange={(event) => {
                  change('checklist', event.target.value);
                }}
              />
              <p className="field__hint" id={`${id}-checklist-hint`}>
                {wording.CAMPAIGN_CHECKLIST_HINT}
              </p>
            </div>
            <RowList<CampaignPlate>
              label={wording.CAMPAIGN_PLATES}
              hint={wording.CAMPAIGN_PLATES_HINT}
              rows={draft.plates}
              empty={{ kind: 'character', name: '', libraryItemId: '' }}
              addLabel={wording.CAMPAIGN_PLATES_ADD}
              onChange={(rows) => {
                change('plates', rows);
              }}
              render={(row, update) => (
                <>
                  <select
                    className="field__input field__input--narrow"
                    aria-label="Shows"
                    value={row.kind}
                    onChange={(event) => {
                      update({ ...row, kind: event.target.value === 'location' ? 'location' : 'character' });
                    }}
                  >
                    <option value="character">Character</option>
                    <option value="location">Location</option>
                  </select>
                  <input
                    className="field__input"
                    type="text"
                    aria-label="Name"
                    placeholder="Name, as in ## Staging"
                    maxLength={80}
                    value={row.name}
                    onChange={(event) => {
                      update({ ...row, name: event.target.value });
                    }}
                  />
                  <input
                    className="field__input"
                    type="text"
                    aria-label="Library item id"
                    placeholder="Library item id"
                    value={row.libraryItemId}
                    onChange={(event) => {
                      update({ ...row, libraryItemId: event.target.value });
                    }}
                  />
                </>
              )}
            />
            {(problem ?? error) !== null && (
              <p className="field__hint field__hint--error" role="alert">
                {problem ?? error}
              </p>
            )}
          </div>
          <div className="dialog__actions">
            {current !== null && (
              <>
                <button
                  type="button"
                  className="button button--danger campaigns-dialog__delete"
                  disabled={busy}
                  onClick={() => {
                    setDeleting(true);
                  }}
                >
                  {wording.CAMPAIGN_DELETE}
                </button>
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  onClick={() => void toggleArchived(current)}
                >
                  {current.archivedAt === null ? wording.CAMPAIGN_ARCHIVE : wording.CAMPAIGN_UNARCHIVE}
                </button>
              </>
            )}
            <button
              type="submit"
              className="button button--primary"
              disabled={busy || draft.name.trim() === '' || problem !== null}
            >
              {busy ? wording.CAMPAIGN_SAVING : wording.CAMPAIGN_SAVE}
            </button>
          </div>
        </form>
      )}
      {current !== null && (
        <ConfirmDialog
          open={deleting}
          title={wording.CAMPAIGN_DELETE_TITLE}
          confirmLabel={wording.CAMPAIGN_DELETE}
          busyLabel={wording.CAMPAIGN_DELETING}
          busy={busy}
          danger
          onConfirm={() => void remove(current)}
          onCancel={() => {
            setDeleting(false);
          }}
        >
          <p>{wording.campaignDeleteBody(current.name, cardCount(current))}</p>
        </ConfirmDialog>
      )}
    </dialog>
  );
}
